/**
 * Customizer add-ons - our extension of the theme's customizer
 *
 * The customizer (assets/component-customizer.js) is another company's code,
 * and they no longer update it. That file is not changed by a single byte:
 * this one wraps two of its methods from outside, and each wrapper calls the
 * original and returns its result untouched unless an add-on is active for
 * that section. Without this script the customizer is exactly as before;
 * with it and no add-on active, it is too.
 *
 * An add-on charges a fee on top of the customizer's own price and puts
 * another variant in the cart. Sy upp (assets/sy-upp.js) is the first: the
 * separate "- måttanpassad höjd" product, whose exact SKU the order needs.
 * A feature registers a provider:
 *
 *   window.HastaCustomizerAddons.register(provider)
 *   provider(sectionId) -> null, or
 *     { variantId, fee (cents), fabric, shippingClass, width, error }
 *
 * The provider is asked at the moment of every calculation, never cached,
 * so every instance of the customizer sees the same answer. Its old
 * instances are never torn down, and they all run on each input.
 *
 * Wrapped:
 * - calculateAdditionalPrices(): the fee is added to the total, but only
 *   when the customizer itself found a height price. A decimal between two
 *   size groups prices the height at 0, and the fee alone must never become
 *   a price. Through that one total, the customizer's own display, hidden
 *   fields and "is there a price" check all include the fee.
 * - handleCustomAddToCart(): the original runs as it is, with its checks,
 *   loading state, errors, notification and cart event. Only the
 *   /cart/add.js request it sends is rewritten, to the add-on's variant plus
 *   what that variant's own product page sends besides the customizer's
 *   values: _Base_Price = the fee, its fabric number and freight class. The
 *   price stays the customizer's own _Custom_Total_Price, which already
 *   holds the fee through the wrapped calculation, so what the shopper sees
 *   is what the line costs. The request goes out synchronously inside the
 *   original call, so the rewrite exists only for that call.
 *
 * What this relies on in component-customizer.js (frozen, 2026-09-29):
 * - ProductCustomizer is a global class, and its methods live on the
 *   prototype, so the wrappers reach old instances as well as new ones
 * - calculateAdditionalPrices() returns { height, total, ... } in cents
 * - every input or change in its fields recalculates at once and writes the
 *   hidden fields, which its add request is built from
 * - handleCustomAddToCart() sends one JSON request,
 *   { items: [{ id, quantity, properties }] }, through window.fetch before
 *   its first await, with _Currency_Code among the properties
 * - window.HastaShipping.applyProperties(properties, enabled, width)
 *
 * Loaded after component-customizer.js (deferred in the layout's head), and
 * only by templates with a feature that registers (sy upp:
 * snippets/curtain-product-template.liquid). Remove that script tag and the
 * customizer runs without it.
 */
(() => {
  if (window.HastaCustomizerAddons) return;
  if (typeof ProductCustomizer === 'undefined') return;

  const proto = ProductCustomizer.prototype;
  const providers = [];

  /** The active add-on for a customizer, or null. A failing provider counts as none. */
  function activeAddon(customizer) {
    for (const provider of providers) {
      try {
        const addon = provider(customizer.sectionId);
        if (addon && addon.variantId && addon.fee > 0) return addon;
      } catch (e) {
        /* a broken add-on never breaks the customizer */
      }
    }
    return null;
  }

  const cartPrice = (value) => (value / 100).toFixed(2);

  /* ---------- the price ---------- */

  const originalPrices = proto.calculateAdditionalPrices;
  proto.calculateAdditionalPrices = function () {
    const prices = originalPrices.apply(this, arguments);
    if (!prices || !(prices.height > 0)) return prices;
    const addon = activeAddon(this);
    if (!addon) return prices;
    return Object.assign({}, prices, { total: prices.total + addon.fee });
  };

  /* ---------- the cart line ---------- */

  const isCartAdd = (resource) => {
    const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  };

  /**
   * The customizer's request, turned into the add-on variant's line. Every
   * value it keeps is the customizer's own: the height as typed, the
   * currency, the height price and the total, fee included. Null if the
   * request is not the customizer's, and then nothing is sent.
   */
  function rewrite(body, addon) {
    const item = body && Array.isArray(body.items) && body.items.length === 1 ? body.items[0] : null;
    if (!item || !item.properties) return null;
    const props = item.properties;

    item.id = Number(addon.variantId);
    if (addon.fabric) props._Fabric_Number = String(addon.fabric);
    else delete props._Fabric_Number;

    // _Base_Price where the variant's own page has it: that page sends its
    // hidden fields in their order (snippets/customizer.liquid), where the
    // base price comes right before the currency. A 0 kr made-to-measure
    // base is left out of the curtain's request, so it is put back there.
    const ordered = {};
    for (const [key, value] of Object.entries(props)) {
      if (key === '_Base_Price') continue;
      if (key === '_Currency_Code') ordered._Base_Price = cartPrice(addon.fee);
      ordered[key] = value;
    }
    if (!('_Base_Price' in ordered)) ordered._Base_Price = cartPrice(addon.fee);
    item.properties = ordered;

    // the freight class by the add-on variant's own width and flag, through
    // the same shared rule the customizer uses
    if (window.HastaShipping && typeof window.HastaShipping.applyProperties === 'function') {
      window.HastaShipping.applyProperties(item.properties, addon.shippingClass === true, addon.width);
    }
    return body;
  }

  const originalAdd = proto.handleCustomAddToCart;
  proto.handleCustomAddToCart = function (form, productFormElement) {
    const addon = activeAddon(this);
    if (!addon) return originalAdd.apply(this, arguments);

    const previous = window.fetch;
    const restore = () => {
      if (window.fetch === rewriting) window.fetch = previous;
    };
    function rewriting(resource, init) {
      if (!isCartAdd(resource)) return previous.apply(window, arguments);
      // one request only: the customizer's add
      restore();
      let body = null;
      try {
        body = init && typeof init.body === 'string' ? rewrite(JSON.parse(init.body), addon) : null;
      } catch (e) {
        body = null;
      }
      if (!body) return Promise.reject(new Error(addon.error || 'Could not add to cart'));
      return previous.call(window, resource, Object.assign({}, init, { body: JSON.stringify(body) }));
    }

    window.fetch = rewriting;
    try {
      return originalAdd.apply(this, arguments);
    } finally {
      // the request, if the checks let one through, went out inside the call
      restore();
    }
  };

  window.HastaCustomizerAddons = {
    register(provider) {
      if (typeof provider === 'function' && !providers.includes(provider)) providers.push(provider);
    }
  };
})();
